// ── Generac Next Generation air-cooled home standby — published specs ────────
// The sizing table the quote brain and the load calculator both read.
//
// WHY THIS FILE EXISTS. Sizing used to run off `load_calc_amps` and a hardcoded
// ladder in playbooks/generator.js (<60A → Silver, 60–90A → 22kW, >90A → 24kW).
// That ladder is really "rated amps on the PROPANE column ≥ demand amps" with
// the fuel left implicit — so every natural-gas job was sized off a number the
// unit cannot make on NG. A 28kW is 28 kW on LP but only 25 kW on natural gas:
// 116.7 A vs 104.2 A at 240 V. Sizing a 105 A NG load onto a "28kW" reads fine
// on the LP column and is 1 A short on the column that actually applies.
//
// GUARDIAN IS GONE. Browning does not sell the Guardian series any more, so it
// is deliberately absent — these are the Next Generation air-cooled units
// (G-Force 800 / 1000), which is what the pricebook SKUs 7258…7327 are.
//
// EVERY ROW CARRIES ITS SOURCE. `source` is where the numbers came from and
// `confidence` is how much to trust them; nothing here is inferred from a model
// number. Rows marked "check" disagree between two sources and are deliberately
// encoded at the CONSERVATIVE value (the smaller rating) so a bad row can only
// ever oversize the generator, never undersize it. Fix a row here, not at the
// call site — and re-run scripts/test_generator_specs.js.
//
// Pure + dependency-free so scripts/test_generator_specs.js can require it
// directly under node (Node 22+ require(esm)). Keep it that way.

// Single-phase 240 V is the only configuration Browning installs air-cooled.
export const VOLTAGE = 240;

// kW → amps at 240 V, 1 phase. Generac's own spec sheets publish "rated
// maximum continuous current" as exactly this quotient, so we derive it rather
// than transcribing a second column that can drift out of step with the kW.
export const ampsFor = (kw) => Math.round(((Number(kw) || 0) * 1000 / VOLTAGE) * 10) / 10;

/**
 * The lineup, smallest first. Ordering matters: pickGenerator walks this array
 * in order and takes the first unit that covers the load.
 *
 *   sku        Generac model number = the pricebook `master_products.sku`.
 *   lp_kw      Rated max continuous output on LIQUID PROPANE.
 *   ng_kw      Rated max continuous output on NATURAL GAS (always ≤ lp_kw).
 *   mlcb_a     Main line circuit breaker fitted in the unit. This — not the kW —
 *              is what the ATS and the feeder have to coordinate with.
 */
export const GENERATOR_SPECS = [
  {
    sku: "7321", label: "10 kW", engine: "G-Force 800",
    lp_kw: 10, ng_kw: 9, mlcb_a: 60, weight_lb: 334,
    bundle: "Ships as a bundle with a 100 A 16-circuit transfer switch.",
    source: "generac.com 10kW (G0073210) + City pricebook description",
    confidence: "check",
    // generac.com's 10 kW page lists 10 kW on BOTH fuels; the City line we
    // actually buy is described "10/9kW". Encoded at 9 kW NG — the smaller of
    // the two — so an NG job sized off this row can only come out bigger.
    note: "NG rating disputed: generac.com says 10 kW, the City catalogue line says 9 kW. Held at 9 kW (conservative). Confirm before quoting an NG job at the top of this unit's range.",
  },
  {
    sku: "7258", label: "14 kW", engine: "G-Force 800",
    lp_kw: 14, ng_kw: 14, mlcb_a: 60, weight_lb: 391,
    source: "generac.com 14kW (G0072580)", confidence: "spec",
  },
  {
    sku: "7259", label: "18 kW", engine: "G-Force 800",
    lp_kw: 18, ng_kw: 17, mlcb_a: 80, weight_lb: 426,
    source: "generac.com 18kW (G0072590)", confidence: "spec",
  },
  {
    sku: "7260", label: "22 kW", engine: "G-Force 1000",
    lp_kw: 22, ng_kw: 21, mlcb_a: 100, weight_lb: 451,
    source: "generac.com 22kW (G0072600)", confidence: "spec",
  },
  {
    sku: "7261", label: "24 kW", engine: "G-Force 1000",
    lp_kw: 24, ng_kw: 22.5, mlcb_a: 100, weight_lb: 461,
    source: "generac.com 24kW (G0072610)", confidence: "spec",
  },
  {
    sku: "7327", label: "26 kW", engine: "G-Force 1000",
    lp_kw: 26, ng_kw: 24, mlcb_a: 110, weight_lb: 524,
    source: "generac.com 26kW (G0073270)", confidence: "spec",
  },
  {
    sku: "7282", label: "28 kW", engine: "G-Force 1000",
    lp_kw: 28, ng_kw: 25, mlcb_a: 125, weight_lb: 524,
    source: "generac.com 28kW (7282)", confidence: "spec",
    // Matches Dylan's own field note on job 2812/2827 ("28/25") and the
    // 125 A MLCB that drove the 3C 2/0 AL feeder on those jobs.
  },
];

export const FUELS = [
  { key: "ng", label: "Natural gas" },
  { key: "lp", label: "Propane" },
];

export const normalizeFuel = (fuel) => {
  const f = String(fuel || "").trim().toLowerCase();
  if (/^(lp|propane|lpg|liquid\s*propane)$/.test(f)) return "lp";
  if (/^(ng|natural\s*gas|nat\s*gas|gas)$/.test(f)) return "ng";
  return null;
};

/**
 * One unit's rating on one fuel. Returns null for an unknown sku/fuel rather
 * than falling back to the propane column — a silent fuel fallback is the exact
 * failure this module exists to prevent.
 */
export function ratingFor(sku, fuel) {
  const spec = GENERATOR_SPECS.find((g) => String(g.sku) === String(sku));
  const f = normalizeFuel(fuel);
  if (!spec || !f) return null;
  const kw = f === "lp" ? spec.lp_kw : spec.ng_kw;
  return {
    sku: spec.sku,
    label: spec.label,
    fuel: f,
    kw,
    watts: Math.round(kw * 1000),
    amps: ampsFor(kw),
    mlcb_a: spec.mlcb_a,
    engine: spec.engine,
    confidence: spec.confidence,
    note: spec.note || null,
    source: spec.source,
  };
}

/** Every unit's rating on one fuel — the table the UI and the AI both show. */
export const ratingTable = (fuel) =>
  GENERATOR_SPECS.map((g) => ratingFor(g.sku, fuel)).filter(Boolean);

/**
 * Pick the smallest unit that carries `demandAmps` on `fuel`.
 *
 * `spareCapacityPct` is spare capacity the tech asks for on top of the
 * calculated demand, NOT a code factor — CEC 8-200 demand is already a
 * diversified number. It defaults to 0 so the result is the honest published
 * rating unless somebody deliberately asks for headroom.
 *
 * Returns the pick plus the neighbours either side, each with headroom, so the
 * tech can overrule it with the trade-off in front of them.
 */
export function pickGenerator({ demandAmps, demandWatts, fuel, spareCapacityPct = 0 } = {}) {
  const f = normalizeFuel(fuel);
  if (!f) return { error: "fuel must be 'ng' or 'lp' — the NG and LP ratings differ on every unit except the 14 kW" };

  let amps = Number(demandAmps);
  if (!Number.isFinite(amps) || amps <= 0) {
    const w = Number(demandWatts);
    if (!Number.isFinite(w) || w <= 0) return { error: "need demandAmps or demandWatts" };
    amps = Math.round((w / VOLTAGE) * 10) / 10;
  }

  const spare = Math.max(0, Number(spareCapacityPct) || 0);
  const required = Math.round(amps * (1 + spare / 100) * 10) / 10;

  const table = ratingTable(f).map((r) => ({
    ...r,
    headroom_a: Math.round((r.amps - amps) * 10) / 10,
    headroom_pct: amps > 0 ? Math.round(((r.amps - amps) / amps) * 1000) / 10 : null,
    covers: r.amps >= required,
  }));

  const idx = table.findIndex((r) => r.covers);
  const pick = idx >= 0 ? table[idx] : null;

  return {
    fuel: f,
    demand_amps: amps,
    demand_watts: Math.round(amps * VOLTAGE),
    spare_capacity_pct: spare,
    required_amps: required,
    pick,
    smaller: idx > 0 ? table[idx - 1] : null,
    larger: idx >= 0 && idx < table.length - 1 ? table[idx + 1] : null,
    // Nothing in the air-cooled lineup covers it — the honest answer is shed
    // more load or move to liquid-cooled, not "round up to the 28".
    over_lineup: idx < 0,
    largest: table[table.length - 1],
    table,
  };
}
